import { useState, useEffect, useCallback } from 'react';
import { Bot, Send, Languages, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

interface SupportMessage {
  id: string;
  sender_kind: 'ai' | 'user';
  sender_name: string | null;
  body: string;
  body_es: string | null;
  created_at: string;
  read_at: string | null;
}

interface SupportConversation {
  id: string;
  subject: string;
  status: string;
  created_at: string;
  messages: SupportMessage[];
  has_unread: boolean;
}

/**
 * Poll only while the tab is actually visible, and fetch immediately when the
 * user comes back to it. A two-minute interval made the modal look broken: a
 * question sent while the app was already open did not appear until the next
 * tick, and the natural reaction is to refresh. Focus is the moment someone is
 * about to look, so that is the moment worth checking.
 *
 * Polling only when visible keeps this from costing anything in the common case
 * — a tech leaves the app open on an iPad all day — so the shorter interval
 * does not translate into steady background load.
 */
const POLL_MS = 30000;
/** Collapse the focus/visibility pair, which both fire on the same tab switch. */
const REFETCH_DEBOUNCE_MS = 2000;

/**
 * "Not now" hides the question for an hour, then it comes back, and it keeps
 * coming back until the person actually answers.
 *
 * It is not a dismissal on purpose: an unanswered question is the entire point
 * of the feature, and a technician who taps past it while carrying a valve is
 * not saying "never ask me this". An hour is long enough to finish what they
 * are doing and short enough that the answer still arrives the same day.
 *
 * Kept in localStorage rather than component state so reloading the page does
 * not reset the snooze — otherwise the way to escape the modal is to refresh,
 * which trains exactly the wrong habit.
 */
const SNOOZE_MS = 60 * 60 * 1000;
const snoozeKey = (conversationId: string) => `irrig_support_snooze_${conversationId}`;

function isSnoozed(conversationId: string): boolean {
  try {
    const until = localStorage.getItem(snoozeKey(conversationId));
    return !!until && Number(until) > Date.now();
  } catch {
    return false; // storage unavailable (private mode) — better to ask than to hide
  }
}

function snoozeUntilLater(conversationId: string): void {
  try {
    localStorage.setItem(snoozeKey(conversationId), String(Date.now() + SNOOZE_MS));
  } catch {
    /* best-effort; without storage it simply reappears on the next poll */
  }
}

function clearSnooze(conversationId: string): void {
  try {
    localStorage.removeItem(snoozeKey(conversationId));
  } catch {
    /* nothing to clean up */
  }
}

/**
 * Surfaces a question the AI developer has asked this user, and collects their
 * answer (feedback 5bdaf927).
 *
 * OUTBOUND ONLY: there is no "start a conversation" affordance, deliberately.
 * The AI only exists while a maintainer's session is running, so a support box
 * a technician could type into at 6am would sit unanswered — worse than not
 * offering one. This appears only when someone has actually been asked
 * something.
 *
 * It opens itself on the first unread question rather than waiting to be
 * clicked: field techs are working, not browsing, and a question they never
 * notice is a question that never gets answered. Once answered or dismissed it
 * stays out of the way until there is genuinely something new.
 *
 * Non-critical, like NotificationBanner — every fetch error is swallowed so
 * this can never break the app for someone trying to do an inspection.
 */
export function SupportChatModal() {
  const { t, i18n } = useTranslation('common');
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [showOther, setShowOther] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await apiClient.get<SupportConversation[]>('/api/support/active');
      setConversations(rows ?? []);
      // Drop snoozes for anything no longer waiting on the user, so the keys
      // do not accumulate in storage for conversations that are done.
      for (const c of rows ?? []) if (c.status !== 'awaiting_user') clearSnooze(c.id);
      return rows ?? [];
    } catch {
      return []; /* silent: never break the chrome */
    }
  }, []);

  /** Waiting on this person, and not snoozed right now. */
  const isDue = (c: SupportConversation) => c.status === 'awaiting_user' && !isSnoozed(c.id);

  useEffect(() => {
    let cancelled = false;
    let lastFetch = 0;

    const tick = async () => {
      if (cancelled) return;
      lastFetch = Date.now();
      const rows = await load();
      // Re-open whenever a question is still waiting and its snooze has run
      // out. Deliberately NOT gated on has_unread: once the modal marks a
      // message read it would never surface again, which is how an unanswered
      // question quietly disappears.
      if (!cancelled && rows.some(isDue)) setOpen(true);
    };

    /** Fetch now unless we just did — focus and visibilitychange both fire on
     *  one tab switch, and a doubled request helps nobody. */
    const refetchSoon = () => {
      if (Date.now() - lastFetch < REFETCH_DEBOUNCE_MS) return;
      void tick();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refetchSoon();
    };

    tick();
    // Skip the tick entirely while hidden: a backgrounded tab polling all day
    // is the cost that made a short interval look expensive.
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void tick();
    }, POLL_MS);

    window.addEventListener('focus', refetchSoon);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', refetchSoon);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  // The oldest question that is actually due — if two are waiting, the one that
  // has been waiting longest deserves the answer. Falls back to any unsnoozed
  // conversation still on the user so opening it manually always shows
  // something sensible.
  const active =
    [...conversations].reverse().find(isDue) ??
    [...conversations].reverse().find((c) => c.status === 'awaiting_user');

  useEffect(() => {
    if (!open || !active || !active.has_unread) return;
    apiClient.post(`/api/support/${active.id}/read`, {}).catch(() => {});
  }, [open, active]);

  if (!open || !active) return null;

  // preferred_locale is unreliable, so every AI message carries both languages
  // and the reader can switch. Show the one matching their UI language by
  // default; offer the other whenever it exists.
  const isSpanishUi = i18n.language?.startsWith('es');
  const pick = (m: SupportMessage) => {
    if (m.sender_kind !== 'ai' || !m.body_es) return m.body;
    const preferred = isSpanishUi ? m.body_es : m.body;
    const other = isSpanishUi ? m.body : m.body_es;
    return showOther ? other : preferred;
  };
  const hasAlternate = active.messages.some((m) => m.sender_kind === 'ai' && m.body_es);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await apiClient.post(`/api/support/${active.id}/reply`, { body: text });
      setDraft('');
      clearSnooze(active.id); // answered — nothing left to come back for
      await load();
      setOpen(false);
    } catch {
      /* leave the text in the box so nothing they typed is lost */
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-surface w-full sm:max-w-lg sm:rounded-xl rounded-t-xl shadow-xl border border-line flex flex-col max-h-[85vh]">
        <div className="flex items-start gap-3 p-4 border-b border-line">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-500/20">
            <Bot size={20} className="text-blue-600 dark:text-blue-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-theme-primary">{t('support.title')}</h3>
            {/* Always says it is an AI. Never presented as a colleague. */}
            <p className="text-xs text-theme-secondary">{t('support.whoAmI')}</p>
          </div>
          {hasAlternate && (
            <button
              type="button"
              onClick={() => setShowOther((v) => !v)}
              className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-300 px-2 py-1 rounded hover:bg-subtle"
            >
              <Languages size={14} />
              {showOther
                ? isSpanishUi
                  ? 'Español'
                  : 'English'
                : isSpanishUi
                  ? 'English'
                  : 'Español'}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {active.messages.map((m) => (
            <div
              key={m.id}
              className={`rounded-lg p-3 text-sm ${
                m.sender_kind === 'ai'
                  ? 'bg-subtle text-theme-primary'
                  : 'bg-blue-600 text-white ml-8'
              }`}
            >
              <p className="whitespace-pre-wrap">{pick(m)}</p>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-line space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            placeholder={t('support.replyPlaceholder')}
            className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="flex items-center justify-between gap-2">
            {/* "Not now" rather than a dismissal: hides for an hour, then the
                question comes back, and keeps coming back until answered. */}
            <button
              type="button"
              onClick={() => {
                snoozeUntilLater(active.id);
                setOpen(false);
              }}
              className="px-3 py-2 text-sm text-theme-secondary hover:text-theme-primary"
            >
              {t('support.notNow')}
            </button>
            <button
              type="button"
              onClick={send}
              disabled={!draft.trim() || sending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {t('support.send')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
